import React, { useEffect, useLayoutEffect, useRef, useState, memo, useCallback } from 'react';
import * as echarts from 'echarts';
import { generateLineChartOption } from './options/generateLineChartOption';
import { generateBarChartOption } from './options/generateBarChartOption';
import { generatePieChartOption } from './options/generatePieChartOption';
import { generateScatterChartOption } from './options/generateScatterChartOption';
import { generateHeatmapChartOption } from './options/generateHeatmapChartOption';
import { generateGaugeChartOption } from './options/generateGaugeChartOption';
import { generateRadarChartOption } from './options/generateRadarChartOption';
import TableRenderer from './TableRenderer';
import NaverMapRenderer from './NaverMapRenderer';
import BadgeRenderer from './BadgeRenderer';
import TextLabelRenderer from './TextLabelRenderer';
import WebFrameRenderer from './WebFrameRenderer';
import BatteryGridRenderer from './BatteryGridRenderer';
import ProgressBarRenderer from './ProgressBarRenderer';
import DatePickerRenderer from './DatePickerRenderer';

/**
 * DynamicChart 컴포넌트 (표준화됨)
 * 
 * 표준 데이터 구조:
 * {
 *   "viz_type": "line",
 *   "data": {
 *     "categories": ["A", "B", "C"],        // X축 (선택사항)
 *     "series": [                           // 데이터 시리즈 (필수)
 *       { "name": "시리즈1", "values": [1,2,3] }
 *     ]
 *   },
 *   "options": {
 *     "title": "차트 제목",
 *     "axes": { "x": "X축", "y": "Y축" },
 *     "style": { "colors": ["#123"] },
 *     "advanced": { ... } // 차트별 고급 옵션
 *   }
 * }
 */
const DynamicChartComponent = ({ chartData, className = '', style = {}, debugMode = false, forceResize = false, isEditMode = false, onChartDoubleClick = null, onChartClick = null, isActiveChart = false, componentId = null, onEmergencyTrigger = null, isClicked, isSelected = false, activeQuery, printMode = false, onRenderComplete = null, resizeDebounceMsOverride = undefined }) => {
  // 디버깅: prop 확인
  if (debugMode) console.log('[DynamicChart Props Debug]', {
    hasChartData: !!chartData,
    chartType: chartData?.viz_type,
    isEditMode,
    printMode,
    hasOnChartClick: !!onChartClick,
    onChartClickType: typeof onChartClick,
    componentId
  });

  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const baseOptionRef = useRef(null);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const resizeObserverRef = useRef(null);
  const resizeTimerRef = useRef(null);
  const lastUpdatedRef = useRef(null);

  // chartData에서 lastUpdated 추출
  const lastUpdated = chartData?.lastUpdated;
  
  // 지원하는 차트 타입 목록
  const SUPPORTED_CHART_TYPES = ['line', 'bar', 'pie', 'scatter', 'heatmap', 'gauge', 'progress_bar', 'radar', 'table', 'navermap', 'badge', 'text_label', 'webframe', 'battery_grid', 'date_picker'];

  // Busy/Ready attribute helper
  const setBusy = useCallback((busy) => {
    try {
      const el = containerRef.current;
      if (!el) return;
      el.setAttribute('aria-busy', busy ? 'true' : 'false');
      el.setAttribute('data-echarts-ready', busy ? 'false' : 'true');
      if (!busy) {
        // bubble so parent containers can aggregate readiness
        el.dispatchEvent(new CustomEvent('chart-ready', { bubbles: true }));
      }
    } catch (_) {}
  }, []);

  // 초기 마운트 시 busy로 표시
  useEffect(() => {
    setBusy(true);
  }, [setBusy]);

  // ResizeObserver loop 에러 핸들링
  const handleResizeObserverError = useCallback((error) => {
    if (error.message && error.message.includes('ResizeObserver loop')) {
      if (debugMode) console.log('[ResizeObserver] Loop detected - ignoring (normal browser behavior)');
      return;
    }
    console.error('[ResizeObserver] Unexpected error:', error);
  }, [debugMode]);

  // 전역 ResizeObserver 에러 핸들러 등록
  useEffect(() => {
    const originalErrorHandler = window.onerror;
    
    window.onerror = (message, source, lineno, colno, error) => {
      if (message && message.includes('ResizeObserver loop')) {
        handleResizeObserverError(error || new Error(message));
        return true; // 에러 전파 중단
      }
      if (originalErrorHandler) {
        return originalErrorHandler(message, source, lineno, colno, error);
      }
      return false;
    };

    return () => {
      window.onerror = originalErrorHandler;
    };
  }, [handleResizeObserverError]);

  // 디바운스된 리사이즈 함수 (편집 모드에서 과도 호출 방지)
  const debouncedResize = useCallback(() => {
    if (resizeTimerRef.current) {
      clearTimeout(resizeTimerRef.current);
    }
    
    // 편집 모드에서 너무 짧은 딜레이는 리사이즈 폭주를 유발 → 기본 60ms, 상위에서 override 가능
    const debounceTime = isEditMode ? (typeof resizeDebounceMsOverride === 'number' ? resizeDebounceMsOverride : 60) : 300;
    
    resizeTimerRef.current = setTimeout(() => {
      if (chartInstanceRef.current && chartRef.current) {
        try {
          // Resize 전에 tooltip 숨기기 - tooltip 에러 방지
          try {
            chartInstanceRef.current.dispatchAction({
              type: 'hideTip'
            });
          } catch (e) {
            // Tooltip 숨기기 실패 시 무시
          }

          chartInstanceRef.current.resize();
          if (baseOptionRef.current) {
            chartInstanceRef.current.setOption(baseOptionRef.current, true);
          }
          if (debugMode) console.log(`[ECharts Resize] Chart resized successfully (${debounceTime}ms delay)`);
        } catch (resizeError) {
          console.error('[ECharts Resize] Error during resize:', resizeError);
        }
      }
    }, debounceTime);
  }, [debugMode, isEditMode]);

  // forceResize prop 변경 시 차트 리사이즈
  useEffect(() => {
    // 🚀 성능 최적화: forceResize가 0이면 스킵 (드래그 중 성능 향상)
    if (forceResize === 0) {
      if (debugMode) console.log('[ECharts Resize] Skipping forced resize (disabled for performance)');
      return;
    }
    
    if (chartInstanceRef.current) {
      setTimeout(() => {
        debouncedResize();
        if (debugMode) console.log('[ECharts Resize] 외부 트리거에 의한 리사이즈 수행');
      }, 10);
    }
  }, [forceResize, debouncedResize, debugMode]);

  // DOM 요소가 실제로 렌더링된 후에 차트 초기화를 수행하기 위한 useLayoutEffect
  useLayoutEffect(() => {
    if (!chartData) {
      if (debugMode) console.log('[ECharts Init] No chart data provided, skipping initialization.');
      setBusy(true); // 데이터 대기 중엔 busy 유지
      return;
    }

    // 테이블 타입은 별도 로직으로 처리
    if (chartData.viz_type === 'table') {
      if (debugMode) console.log('[Table Render] Type is table, skipping ECharts logic.');
      setBusy(false);
      return;
    }

    // 네이버 지도 타입은 별도 로직으로 처리
    if (chartData.viz_type === 'navermap') {
      if (debugMode) console.log('[NaverMap Render] Type is navermap, skipping ECharts logic.');
      setBusy(false);
      return;
    }

    // 배지 타입은 별도 로직으로 처리
    if (chartData.viz_type === 'badge') {
      if (debugMode) console.log('[Badge Render] Type is badge, skipping ECharts logic.');
      setBusy(false);
      return;
    }

    // 텍스트 라벨 타입은 별도 로직으로 처리
    if (chartData.viz_type === 'text_label') {
      if (debugMode) console.log('[TextLabel Render] Type is text_label, skipping ECharts logic.');
      setBusy(false);
      return;
    }

    // 웹프레임 타입은 별도 로직으로 처리
    if (chartData.viz_type === 'webframe') {
      if (debugMode) console.log('[WebFrame Render] Type is webframe, skipping ECharts logic.');
      setBusy(false);
      return;
    }

    // 배터리 그리드 타입은 별도 로직으로 처리
    if (chartData.viz_type === 'battery_grid') {
      if (debugMode) console.log('[BatteryGrid Render] Type is battery_grid, skipping ECharts logic.');
      setBusy(false);
      return;
    }

    // 프로그레스 바 타입은 별도 로직으로 처리
    if (chartData.viz_type === 'progress_bar') {
      if (debugMode) console.log('[ProgressBar Render] Type is progress_bar, skipping ECharts logic.');
      setBusy(false);
      return;
    }

    if (!chartRef.current) {
      if (debugMode) console.log('[ECharts Init] Chart container not found in DOM, skipping initialization.');
      setBusy(true);
      return;
    }

    // 이미 초기화된 인스턴스가 있으면 데이터만 업데이트
    if (chartInstanceRef.current) {
      if (debugMode) console.log('[ECharts Update] Updating existing chart with new data.');
      try {
        // Tooltip 숨기기 - 차트 업데이트 전에 tooltip 정리
        try {
          chartInstanceRef.current.dispatchAction({
            type: 'hideTip'
          });
        } catch (e) {
          // Tooltip 숨기기 실패 시 무시
        }

        const option = generateStandardizedChartOption(chartData, isEditMode);
        chartInstanceRef.current.setOption(option, true);
        baseOptionRef.current = option;
        if (debugMode) console.log('[ECharts Update] Chart data updated successfully.');
      } catch (updateError) {
        console.error('[ECharts Update] Error updating chart:', updateError);
        setError(`차트 업데이트 중 오류 발생: ${updateError.message}`);
      }
      return;
    }

    try {
      const containerWidth = chartRef.current.clientWidth;
      const containerHeight = chartRef.current.clientHeight;
      
      if (debugMode) {
        console.log(`[ECharts Init] Initializing for ${chartData.viz_type} (${chartData.options?.title}). Container dimensions: ${containerWidth} x ${containerHeight}`);
      }

      if (containerWidth === 0 || containerHeight === 0) {
        if (debugMode) console.log('[ECharts Init] Container has zero dimensions, setting minimum height and retrying...');
        
        chartRef.current.style.minHeight = '250px';
        
        setTimeout(() => {
          const updatedWidth = chartRef.current.clientWidth;
          const updatedHeight = chartRef.current.clientHeight;
          
          if (debugMode) {
            console.log(`[ECharts Init] Retrying with updated dimensions: ${updatedWidth} x ${updatedHeight}`);
          }
          
          if (updatedWidth === 0 || updatedHeight === 0) {
            console.error('[ECharts Init] Container still has zero dimensions after retry, cannot initialize chart.');
            return;
          }
          
          initializeChart();
        }, 0);
        return;
      }
      
      initializeChart();
      
    } catch (error) {
      console.error('[ECharts Init] Error during initialization:', error);
      setError(`차트 초기화 중 오류 발생: ${error.message}`);
    }
    
    // 개선된 리사이즈 옵저버 설정 (편집 모드에서도 활성화)
    if ('ResizeObserver' in window && chartRef.current) {
      try {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }

        let resizeDebounceTimer;
        resizeObserverRef.current = new ResizeObserver((entries) => {
          try {
            // ResizeObserver loop 방지를 위한 디바운싱
            clearTimeout(resizeDebounceTimer);
            resizeDebounceTimer = setTimeout(() => {
              if (chartInstanceRef.current && entries && entries.length > 0) {
                // 크기 변화가 실제로 있는지 확인
                const entry = entries[0];
                const newWidth = Math.round(entry.contentRect.width);
                const newHeight = Math.round(entry.contentRect.height);
                const currentWidth = Math.round(chartRef.current?.clientWidth || 0);
                const currentHeight = Math.round(chartRef.current?.clientHeight || 0);

                // 5픽셀 이상 차이날 때만 리사이즈
                if (Math.abs(newWidth - currentWidth) > 5 || Math.abs(newHeight - currentHeight) > 5) {
                  debouncedResize();
                  if (debugMode) console.log('[ResizeObserver] Container size change detected');
                }
              }
            }, 100); // 100ms 디바운싱
          } catch (observerError) {
            handleResizeObserverError(observerError);
          }
        });

        resizeObserverRef.current.observe(chartRef.current);

        if (debugMode) console.log('[ResizeObserver] Successfully initialized');
      } catch (observerSetupError) {
        console.error('[ResizeObserver] Setup failed:', observerSetupError);
        setupWindowResizeHandler();
      }
    } else {
      setupWindowResizeHandler();
    }
    
    function setupWindowResizeHandler() {
      const handleWindowResize = () => debouncedResize();
      window.addEventListener('resize', handleWindowResize, { passive: true });
      
      return () => {
        window.removeEventListener('resize', handleWindowResize);
      };
    }
    
    // 차트 초기화 함수
    function initializeChart() {
      try {
        if (!SUPPORTED_CHART_TYPES.includes(chartData.viz_type)) {
          setError(`'${chartData.viz_type}' 차트 유형은 현재 지원되지 않습니다.`);
          return;
        }
        
        const chartInstance = echarts.init(chartRef.current, null, {
          renderer: 'canvas',
          useDirtyRect: true,
          locale: 'KR',
          devicePixelRatio: window.devicePixelRatio || 1,
          // 성능 최적화 옵션
          useCoarsePointer: false,
          pointerSize: 'auto',
          ssr: false
        });
        
        chartInstanceRef.current = chartInstance;
        
        // 표준화된 차트 옵션 생성 및 설정
        const option = generateStandardizedChartOption(chartData, isEditMode);
        setBusy(true);

        // 차트 옵션 설정 (툴팁 포함)
        chartInstance.setOption(option, true);
        baseOptionRef.current = option;

        if (debugMode) console.log('[ECharts Render] Chart rendered successfully.');

        // 차트 옵션 설정 후에 이벤트 리스너 등록 (중요!)
        if (debugMode) console.log('[ECharts Event Setup Debug]', {
          printMode,
          isEditMode,
          hasOnChartClick: !!onChartClick,
          onChartClickType: typeof onChartClick,
          willRegisterClickListener: !printMode && !isEditMode && onChartClick && typeof onChartClick === 'function',
          willRegisterDoubleClickListener: !printMode && isEditMode && onChartDoubleClick && typeof onChartDoubleClick === 'function'
        });

        // 편집 모드에서 더블클릭 이벤트 처리
        if (!printMode && isEditMode && onChartDoubleClick && typeof onChartDoubleClick === 'function') {
          const handleDoubleClick = function(event) {
            if (debugMode) console.log('[ECharts] 더블클릭 이벤트 감지됨 - 편집 모달 열기');
            event.stop(); // 기본 동작 방지
            onChartDoubleClick();
          };

          chartInstance.getZr().on('dblclick', handleDoubleClick);

          if (debugMode) console.log('[ECharts] 더블클릭 이벤트 리스너 등록 완료');
        }

        // 차트 데이터 클릭 이벤트 처리 (런타임 모드) - 옵션 설정 후에 등록!
        if (!printMode && !isEditMode && onChartClick && typeof onChartClick === 'function') {
          if (debugMode) console.log('[ECharts Init] ✅ Starting to register click event listeners for chart type:', chartData.viz_type);

          // 지연 후 이벤트 리스너 등록 (ECharts가 완전히 초기화되도록)
          setTimeout(() => {
            if (!chartInstance || !chartInstance.getDom()) {
            if (debugMode) console.warn('[ECharts Init] ❌ Chart instance not ready');
              return;
            }

            try {
              // 먼저 차트 인스턴스 상태 확인
              if (debugMode) console.log('[ECharts Init] Chart instance status:', {
                isDisposed: chartInstance.isDisposed ? chartInstance.isDisposed() : 'N/A',
                dom: !!chartInstance.getDom(),
                width: chartInstance.getWidth(),
                height: chartInstance.getHeight()
              });

              // 기존 이벤트 리스너 모두 제거
              chartInstance.off('click');

              // 새로운 클릭 이벤트 등록
              chartInstance.on('click', function(params) {
                if (debugMode) console.log('[ECharts Click] ✅✅✅ CLICKED!', params);

                // 라인차트는 ZRender 경로로만 처리하여 중복/오차 방지
                if (chartData.viz_type === 'line') {
                  if (debugMode) console.log('[ECharts Click] Skip handling for line chart (handled by ZRender)');
                  return;
                }

                if (!params) {
                  if (debugMode) console.log('[ECharts Click] No params - background click');
                  return;
                }

                const clickedData = {
                  seriesName: params.seriesName || '',
                  name: params.name || (params.data && params.data.name) || '',
                  value: params.value || (params.data && params.data.value) || null,
                  dataIndex: params.dataIndex,
                  seriesIndex: params.seriesIndex,
                  componentType: params.componentType || 'unknown',
                  chartType: chartData.viz_type,
                  data: params.data || null
                };

                if (debugMode) console.log('[ECharts Click] Processed data:', clickedData);

                if (onChartClick && typeof onChartClick === 'function') {
                  onChartClick(clickedData, 'self');
                } else {
                  console.error('[ECharts Click] onChartClick is not a function!');
                }
              });

              // getZr()로도 클릭 이벤트 등록 (백업)
              const zr = chartInstance.getZr();
              if (zr) {
                zr.on('click', function(event) {
                  if (debugMode) console.log('[ECharts ZRender] ZR Click detected', event);

                  // Tooltip 숨기기 - 클릭 시 tooltip 에러 방지
                  try {
                    if (chartInstance) {
                      chartInstance.dispatchAction({
                        type: 'hideTip'
                      });
                    }
                  } catch (e) {
                    // Tooltip 숨기기 실패 시 무시
                    if (debugMode) console.log('[ECharts ZRender] Failed to hide tooltip on click:', e);
                  }

                  if (debugMode) console.log('[ECharts ZRender] Context:', {
                    isEditMode: isEditMode,
                    hasOnChartClick: !!onChartClick,
                    chartType: chartData.viz_type
                  });

                  // PiePiece의 경우 dataIndex 탐색
                  if (event.target && event.target.type === 'sector') {
                    if (debugMode) console.log('[ECharts ZRender] PiePiece exploration:', {
                      allKeys: Object.keys(event.target),
                      __ecComponentInfo: event.target.__ecComponentInfo,
                      ecData: event.target.ecData
                    });
                  }

                  // 편집 모드에서는 클릭 이벤트 무시
                  if (isEditMode) {
                    if (debugMode) console.log('[ECharts ZRender] Edit mode - ignoring click');
                    return;
                  }

                  // ZRender 이벤트에서 직접 처리
                  if (event.target) {
                    if (debugMode) console.log('[ECharts ZRender] Target details:', {
                      type: event.target.type,
                      dataIndex: event.target.dataIndex,
                      seriesIndex: event.target.seriesIndex,
                      shape: event.target.shape
                    });

                    // 파이차트 처리 - angle 기반으로 데이터 찾기
                    if (chartData.viz_type === 'pie' && event.target.type === 'sector') {
                      if (debugMode) console.log('[ECharts ZRender] Processing pie click by angle');
                      const option = chartInstance.getOption();
                      const seriesData = option.series[0].data;

                      // shape의 각도 정보를 이용해 어떤 데이터인지 찾기
                      const targetStartAngle = event.target.shape.startAngle;
                      const targetEndAngle = event.target.shape.endAngle;

                      // 각 데이터의 각도를 계산하여 매칭
                      let clickedIndex = -1;
                      let currentAngle = -Math.PI / 2; // ECharts 파이 차트는 12시 방향에서 시작
                      const total = seriesData.reduce((sum, item) => sum + item.value, 0);

                      for (let i = 0; i < seriesData.length; i++) {
                        const item = seriesData[i];
                        const angle = (item.value / total) * Math.PI * 2;
                        const itemStartAngle = currentAngle;
                        const itemEndAngle = currentAngle + angle;

                        // 각도 비교 (부동소수점 오차 고려)
                        if (Math.abs(itemStartAngle - targetStartAngle) < 0.01 &&
                            Math.abs(itemEndAngle - targetEndAngle) < 0.01) {
                          clickedIndex = i;
                          break;
                        }
                        currentAngle = itemEndAngle;
                      }

                      if (clickedIndex >= 0) {
                        const clickedItem = seriesData[clickedIndex];
                        const clickedData = {
                          seriesName: option.series[0].name || '',
                          name: clickedItem.name,
                          value: clickedItem.value,
                          dataIndex: event.target.dataIndex,
                          seriesIndex: 0,
                          componentType: 'series',
                          chartType: chartData.viz_type,
                          data: clickedItem
                        };

                        if (debugMode) console.log('[ECharts ZRender] Processing pie click:', clickedData);

                        if (onChartClick && typeof onChartClick === 'function') {
                          onChartClick(clickedData, 'self');
                        }
                      }
                    }
                    // 바차트 처리
                    else if (chartData.viz_type === 'bar' && event.target.dataIndex !== undefined) {
                      const option = chartInstance.getOption();
                      // ecData 우선 사용하여 시리즈/데이터 인덱스 탐지
                      const ecData = event.target.ecData || event.target.__ecComponentInfo || {};
                      const seriesIndex = (ecData.seriesIndex !== undefined ? ecData.seriesIndex : event.target.seriesIndex) || 0;
                      const dataIndex = (ecData.dataIndex !== undefined ? ecData.dataIndex : event.target.dataIndex) || 0;
                      const series = option.series[seriesIndex];
                      const categories = option.xAxis.data || option.xAxis[0].data;

                      const clickedData = {
                        seriesName: series?.name || '',
                        name: categories ? categories[dataIndex] : '',
                        value: series?.data ? series.data[dataIndex] : undefined,
                        dataIndex: dataIndex,
                        seriesIndex: seriesIndex,
                        componentType: 'series',
                        chartType: chartData.viz_type,
                        data: null
                      };

                      console.log('[ECharts ZRender] Processing bar click:', clickedData);

                      if (onChartClick && typeof onChartClick === 'function') {
                        onChartClick(clickedData, 'self');
                      }
                    }
                    // 바차트 처리 (rect이지만 dataIndex가 없는 경우 - 그룹 내 시리즈 추정)
                    else if (chartData.viz_type === 'bar' && event.target.type === 'rect') {
                      const option = chartInstance.getOption();
                      const categories = option.xAxis[0]?.data || option.xAxis?.data || [];
                      const seriesCount = (option.series || []).length;

                      const clickedX = event.target.shape?.x || 0;
                      const clickedWidth = event.target.shape?.width || 0;
                      const barCenterX = clickedX + clickedWidth / 2;

                      // 그리드/카테고리 영역 추정 (렌더에서 사용한 비율과 동일 가정)
                      const chartWidth = chartInstance.getWidth();
                      const gridLeft = chartWidth * 0.1;
                      const gridRight = chartWidth * 0.9;
                      const gridWidth = gridRight - gridLeft;
                      const categoryCount = categories.length || 1;
                      const categoryWidth = gridWidth / categoryCount;

                      // 카테고리 인덱스 탐지
                      let clickedIndex = -1;
                      for (let i = 0; i < categoryCount; i++) {
                        const groupStart = gridLeft + (i * categoryWidth) + (categoryWidth * 0.2);
                        const groupEnd = groupStart + (categoryWidth * 0.6);
                      const inRange = barCenterX >= groupStart - 2 && barCenterX <= groupEnd + 2;
                        if (inRange) {
                          clickedIndex = i;
                          break;
                        }
                      }

                      if (clickedIndex < 0) {
                        if (debugMode) console.log('[ECharts ZRender] Could not detect category index for rect bar.');
                        return;
                      }

                      // 그룹 내 시리즈 인덱스 추정
                      const groupStart = gridLeft + (clickedIndex * categoryWidth) + (categoryWidth * 0.2);
                      const groupWidth = categoryWidth * 0.6;
                      const perSeriesWidth = groupWidth / Math.max(seriesCount, 1);
                      let seriesIndex = Math.floor((barCenterX - groupStart) / perSeriesWidth);
                      if (seriesIndex < 0) seriesIndex = 0;
                      if (seriesIndex > seriesCount - 1) seriesIndex = seriesCount - 1;

                      const series = option.series[seriesIndex] || {};
                      const rawValue = (series.data || [])[clickedIndex];
                      const actualValue = (typeof rawValue === 'object' && rawValue !== null && 'value' in rawValue) ? rawValue.value : rawValue;

                      const clickedData = {
                        seriesName: series.name || '',
                        name: categories[clickedIndex],
                        value: actualValue,
                        dataIndex: clickedIndex,
                        seriesIndex: seriesIndex,
                        componentType: 'series',
                        chartType: chartData.viz_type,
                        data: { name: categories[clickedIndex], value: actualValue }
                      };

                      if (debugMode) console.log('[ECharts ZRender] Processing bar click (rect without indices):', clickedData);

                      if (onChartClick && typeof onChartClick === 'function') {
                        onChartClick(clickedData, 'self');
                      }
                    }
                    // 라인차트 처리 - convertToPixel 기반 최근접 인덱스 매칭
                    else if (chartData.viz_type === 'line' && (event.target.type === 'circle' || event.target.type === 'path')) {
                      const option = chartInstance.getOption();
                      const xAxisData = option.xAxis[0]?.data || option.xAxis?.data;
                      const seriesData = option.series[0]?.data || [];

                      // 클릭 X 픽셀 좌표 계산 (우선 event.offsetX)
                      const domRect = chartInstance.getDom().getBoundingClientRect();
                      let clickX = (event.event && typeof event.event.offsetX === 'number')
                        ? event.event.offsetX
                        : (event.event && typeof event.event.pageX === 'number')
                          ? (event.event.pageX - domRect.left)
                          : (event.target.shape && typeof event.target.shape.cx === 'number')
                            ? event.target.shape.cx
                            : 0;

                      const pointCount = xAxisData ? xAxisData.length : seriesData.length;
                      let clickedIndex = 0;
                      let minDist = Number.POSITIVE_INFINITY;

                      for (let i = 0; i < pointCount; i++) {
                        const px = chartInstance.convertToPixel({ seriesIndex: 0 }, [i, 0]);
                        const pixelX = Array.isArray(px) ? px[0] : px;
                        const dist = Math.abs(pixelX - clickX);
                        if (dist < minDist) {
                          minDist = dist;
                          clickedIndex = i;
                        }
                      }

                      // 값 추출 (배열/객체 호환)
                      const rawValue = seriesData[clickedIndex];
                      let actualValue;
                      let xValue = xAxisData ? xAxisData[clickedIndex] : clickedIndex;
                      if (typeof rawValue === 'object' && rawValue !== null) {
                        if (Array.isArray(rawValue)) {
                          xValue = rawValue[0];
                          actualValue = rawValue[1];
                        } else if ('value' in rawValue) {
                          if (Array.isArray(rawValue.value)) {
                            xValue = rawValue.value[0];
                            actualValue = rawValue.value[1];
                          } else {
                            actualValue = rawValue.value;
                          }
                        } else {
                          actualValue = rawValue;
                        }
                      } else {
                        actualValue = rawValue;
                      }

                      const clickedData = {
                        seriesName: option.series[0]?.name || '',
                        name: xValue,
                        value: actualValue,
                        dataIndex: clickedIndex,
                        seriesIndex: 0,
                        componentType: 'series',
                        chartType: 'line',
                        data: { name: xValue, value: actualValue }
                      };

                      if (onChartClick && typeof onChartClick === 'function') {
                        onChartClick(clickedData, 'self');
                      }
                    }
                    // 라인차트 처리 - convertFromPixel 기반 최근접 인덱스 매핑
                    else if (chartData.viz_type === 'line' && (event.target.type === 'circle' || event.target.type === 'path')) {
                      const option = chartInstance.getOption();
                      const xAxisData = option.xAxis[0]?.data || option.xAxis?.data;
                      const seriesData = option.series[0]?.data || [];

                      // 클릭 좌표 (픽셀)
                      const domRect = chartInstance.getDom().getBoundingClientRect();
                      const clickX = (typeof event.offsetX === 'number')
                        ? event.offsetX
                        : (typeof event.zrX === 'number')
                          ? event.zrX
                          : (event.event && typeof event.event.pageX === 'number')
                            ? (event.event.pageX - domRect.left)
                            : 0;
                      const clickY = (typeof event.offsetY === 'number')
                        ? event.offsetY
                        : (typeof event.zrY === 'number')
                          ? event.zrY
                          : (event.event && typeof event.event.pageY === 'number')
                            ? (event.event.pageY - domRect.top)
                            : 0;

                      // 픽셀→데이터 좌표 변환
                      const dataCoord = chartInstance.convertFromPixel({ seriesIndex: 0 }, [clickX, clickY]);
                      let clickedIndex = 0;
                      if (Array.isArray(dataCoord)) {
                        if (typeof dataCoord[0] === 'number' && !Number.isNaN(dataCoord[0])) {
                          clickedIndex = Math.round(dataCoord[0]);
                        } else if (xAxisData && typeof dataCoord[0] === 'string') {
                          const idx = xAxisData.indexOf(dataCoord[0]);
                          clickedIndex = idx >= 0 ? idx : 0;
                        }
                      }
                      // 범위 보정
                      const pointCount = xAxisData ? xAxisData.length : seriesData.length;
                      if (clickedIndex < 0) clickedIndex = 0;
                      if (clickedIndex > pointCount - 1) clickedIndex = pointCount - 1;

                      // 값 추출
                      const rawValue = seriesData[clickedIndex];
                      let actualValue;
                      let xValue = xAxisData ? xAxisData[clickedIndex] : clickedIndex;
                      if (typeof rawValue === 'object' && rawValue !== null) {
                        if (Array.isArray(rawValue)) {
                          xValue = rawValue[0];
                          actualValue = rawValue[1];
                        } else if ('value' in rawValue) {
                          if (Array.isArray(rawValue.value)) {
                            xValue = rawValue.value[0];
                            actualValue = rawValue.value[1];
                          } else {
                            actualValue = rawValue.value;
                          }
                        } else {
                          actualValue = rawValue;
                        }
                      } else {
                        actualValue = rawValue;
                      }

                      const clickedData = {
                        seriesName: option.series[0]?.name || '',
                        name: xValue,
                        value: actualValue,
                        dataIndex: clickedIndex,
                        seriesIndex: 0,
                        componentType: 'series',
                        chartType: 'line',
                        data: { name: xValue, value: actualValue }
                      };

                      if (onChartClick && typeof onChartClick === 'function') {
                        onChartClick(clickedData, 'self');
                      }
                    }
                  } else {
                    // 배경 클릭
                    if (debugMode) console.log('[ECharts ZRender] Background click');
                    const clickedData = {
                      seriesName: '',
                      name: '',
                      value: null,
                      dataIndex: 0,
                      seriesIndex: 0,
                      componentType: 'background',
                      chartType: chartData.viz_type,
                      data: null
                    };

                    if (onChartClick && typeof onChartClick === 'function') {
                      onChartClick(clickedData, 'self');
                    }
                  }
                });
              }

              if (debugMode) console.log('[ECharts Init] ✅ Click event listener registered after delay!');

              // 이벤트 핸들러 확인
              const handlers = chartInstance._$handlers;
              if (debugMode) console.log('[ECharts Init] Registered handlers:', handlers ? Object.keys(handlers) : 'none');

            } catch (error) {
              console.error('[ECharts Init] ❌ Failed to register click event listener:', error);
            }
          }, 100); // 100ms 지연
        } else {
          if (debugMode) console.log('[ECharts Init] ❌ Click listeners NOT registered due to conditions');
        }
        // 렌더 애니메이션까지 완료되었을 때 ready 처리
        const handleFinished = () => {
          setBusy(false);
          try { chartInstance.off('finished', handleFinished); } catch (_) {}
        };
        try { chartInstance.on('finished', handleFinished); } catch (_) { setBusy(false); }
        
      } catch (renderError) {
        console.error('[ECharts Render] Error:', renderError);
        setError(`차트 렌더링 중 오류 발생: ${renderError.message}`);
        setBusy(false);
      }
    }
    
    // 컴포넌트 언마운트 시 정리 함수 반환
    return () => {
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
      
      if (resizeObserverRef.current) {
        try {
          resizeObserverRef.current.disconnect();
        } catch (disconnectError) {
          console.warn('[ResizeObserver] Disconnect error:', disconnectError);
        }
        resizeObserverRef.current = null;
      }
      
      if (chartInstanceRef.current) {
        try {
          // 차트가 여전히 DOM에 연결되어 있는지 확인
          if (chartInstanceRef.current.getDom() && chartInstanceRef.current.getDom().parentNode) {
            // 모든 컴포넌트의 하이라이트 제거
            try {
              chartInstanceRef.current.dispatchAction({
                type: 'downplay'
              });
            } catch (e) {
              // 하이라이트 제거 실패 시 무시
            }

            // Tooltip 숨기기 - 에러 방지를 위해 추가
            try {
              chartInstanceRef.current.dispatchAction({
                type: 'hideTip'
              });
            } catch (e) {
              // Tooltip 숨기기 실패 시 무시
              if (debugMode) console.log('[ECharts Dispose] Failed to hide tooltip:', e);
            }
          }

          // 이벤트 리스너 제거
          try {
            chartInstanceRef.current.getZr().off('dblclick');
            chartInstanceRef.current.getZr().off('click');
            chartInstanceRef.current.off('click');
          } catch (e) {
            // 이벤트 리스너 제거 실패 시 무시
          }

          // 차트 인스턴스 정리
          chartInstanceRef.current.clear();
          chartInstanceRef.current.dispose();
        } catch (disposeError) {
          console.warn('[ECharts] Dispose error:', disposeError);
        }
        chartInstanceRef.current = null;
      }
    };
  }, [debugMode, debouncedResize, handleResizeObserverError, isEditMode, printMode]); // 의존성 최소화

  // 편집 모드 및 클릭 이벤트 리스너 업데이트
  useEffect(() => {
    if (!chartInstanceRef.current) return;

    // 편집 모드로 변경될 때 처리
    // 툴팁 관련 처리 제거 (에러 방지)

    // 기존 이벤트 리스너 제거
    chartInstanceRef.current.getZr().off('dblclick');
    chartInstanceRef.current.getZr().off('click');
    chartInstanceRef.current.off('click');
    chartInstanceRef.current.off('click', 'series');

    // 편집 모드에서 더블클릭 이벤트 리스너 등록
    if (!printMode && isEditMode && onChartDoubleClick && typeof onChartDoubleClick === 'function') {
      chartInstanceRef.current.getZr().on('dblclick', function(event) {
        console.log('[ECharts] 더블클릭 이벤트 감지됨 - 편집 모달 열기');
        event.stop(); // 기본 동작 방지
        onChartDoubleClick();
      });

      if (debugMode) console.log('[ECharts] 더블클릭 이벤트 리스너 재등록 완료');
    }

    // 런타임 모드에서 클릭 이벤트 리스너 등록
    if (!printMode && !isEditMode && onChartClick && typeof onChartClick === 'function') {
      if (debugMode) console.log('[ECharts Update] ✅ Re-registering click event listeners for chart type:', chartData?.viz_type);

      // 지연 후 재등록
      setTimeout(() => {
        if (!chartInstanceRef.current || !chartInstanceRef.current.getDom()) {
          if (debugMode) console.warn('[ECharts Update] Chart instance not ready');
          return;
        }

        try {
          // 기존 이벤트 리스너 제거
          chartInstanceRef.current.off('click');

          // 새로운 클릭 이벤트 등록
          chartInstanceRef.current.on('click', function(params) {
            if (debugMode) console.log('[ECharts Click Update] ✅✅✅ CLICKED!', params);

            const clickedData = {
              seriesName: params.seriesName || '',
              name: params.name || (params.data && params.data.name) || '',
              value: params.value || (params.data && params.data.value) || null,
              dataIndex: params.dataIndex,
              seriesIndex: params.seriesIndex,
              componentType: params.componentType || 'unknown',
              chartType: chartData?.viz_type,
              data: params.data || null
            };

            console.log('[ECharts Click Update] Processed data:', clickedData);

            if (onChartClick && typeof onChartClick === 'function') {
              onChartClick(clickedData, 'self');
            }
          });

          // ZRender 레벨 이벤트도 등록
          const zr = chartInstanceRef.current.getZr();
          if (zr) {
            zr.off('click'); // 기존 리스너 제거
            zr.on('click', function(event) {
              console.log('[ECharts ZRender Update] ZR Click detected', event);

              // Tooltip 숨기기 - 클릭 시 tooltip 에러 방지
              try {
                if (chartInstanceRef.current) {
                  chartInstanceRef.current.dispatchAction({
                    type: 'hideTip'
                  });
                }
              } catch (e) {
                // Tooltip 숨기기 실패 시 무시
                if (debugMode) console.log('[ECharts ZRender] Failed to hide tooltip on click:', e);
              }

              console.log('[ECharts ZRender Update] Target info:', {
                hasTarget: !!event.target,
                targetType: event.target?.type,
                dataIndex: event.target?.dataIndex,
                seriesIndex: event.target?.seriesIndex,
                isEditMode: isEditMode,
                chartType: chartData?.viz_type
              });

              // Path (라인차트 선)의 경우 확인
              if (event.target && event.target.type === 'path') {
                console.log('[ECharts ZRender Update] Path (Line) target properties:', {
                  allKeys: Object.keys(event.target),
                  shape: event.target.shape,
                  __ecComponentInfo: event.target.__ecComponentInfo,
                  dataIndex: event.target.dataIndex,
                  ecData: event.target.ecData,
                  seriesIndex: event.target.seriesIndex,
                  parent: event.target.parent,
                  topTarget: event.topTarget
                });
              }

              // Circle (라인차트 점)의 경우 확인
              if (event.target && event.target.type === 'circle') {
                console.log('[ECharts ZRender Update] Circle (Line) target properties:', {
                  allKeys: Object.keys(event.target),
                  shape: event.target.shape,
                  __ecComponentInfo: event.target.__ecComponentInfo,
                  dataIndex: event.target.dataIndex,
                  ecData: event.target.ecData,
                  seriesIndex: event.target.seriesIndex
                });

                if (event.target.shape) {
                  console.log('[ECharts ZRender Update] Circle shape details:', {
                    cx: event.target.shape.cx,
                    cy: event.target.shape.cy,
                    r: event.target.shape.r
                  });
                }
              }

              // Rect (바차트)의 경우 확인
              if (event.target && event.target.type === 'rect') {
                console.log('[ECharts ZRender Update] Rect (Bar) target properties:', {
                  allKeys: Object.keys(event.target),
                  shape: event.target.shape,
                  __ecComponentInfo: event.target.__ecComponentInfo,
                  dataIndex: event.target.dataIndex,
                  ecData: event.target.ecData,
                  seriesIndex: event.target.seriesIndex
                });

                if (event.target.shape) {
                  console.log('[ECharts ZRender Update] Rect shape details:', {
                    x: event.target.shape.x,
                    y: event.target.shape.y,
                    width: event.target.shape.width,
                    height: event.target.shape.height
                  });
                }
              }

              // PiePiece의 경우 dataIndex가 다른 위치에 있을 수 있음
              if (event.target && event.target.type === 'sector') {
                // 모든 속성 확인
                const allProps = {};
                Object.keys(event.target).forEach(key => {
                  const value = event.target[key];
                  // 함수가 아니고 큰 객체가 아닌 경우만 로깅
                  if (typeof value !== 'function' && key !== 'shape' && key !== 'style') {
                    allProps[key] = value;
                  }
                });

                console.log('[ECharts ZRender Update] PiePiece all properties:', allProps);
                console.log('[ECharts ZRender Update] Shape details:', event.target.shape);

                // 특별히 확인할 속성들
                console.log('[ECharts ZRender Update] Special checks:', {
                  dataIndex: event.target.dataIndex,
                  __ecData: event.target.__ecData,
                  __ec_inner__: event.target.__ec_inner__,
                  __ecComponentInfo: event.target.__ecComponentInfo,
                  z2: event.target.z2,
                  zlevel: event.target.zlevel,
                  silent: event.target.silent
                });

                // shape에서 데이터 찾기
                if (event.target.shape) {
                  console.log('[ECharts ZRender Update] Shape properties that might have data:', {
                    r0: event.target.shape.r0,
                    r: event.target.shape.r,
                    startAngle: event.target.shape.startAngle,
                    endAngle: event.target.shape.endAngle,
                    clockwise: event.target.shape.clockwise
                  });
                }
              }

              // 편집 모드에서는 클릭 이벤트 무시
              if (isEditMode) {
                console.log('[ECharts ZRender Update] Edit mode - ignoring click');
                return;
              }

              // 파이차트는 dataIndex가 없으므로 angle로 계산
              if (event.target && event.target.type === 'sector' && chartData?.viz_type === 'pie') {
                console.log('[ECharts ZRender Update] Processing pie click by angle');
                const option = chartInstanceRef.current.getOption();
                const seriesData = option.series[0].data;

                // shape의 각도 정보를 이용해 어떤 데이터인지 찾기
                let targetStartAngle = event.target.shape.startAngle;
                let targetEndAngle = event.target.shape.endAngle;

                // 각도 정규화 함수 (0 ~ 2π 범위로 변환)
                const normalizeAngle = (angle) => {
                  let normalized = angle % (Math.PI * 2);
                  if (normalized < 0) normalized += Math.PI * 2;
                  return normalized;
                };

                // 타겟 각도 정규화
                targetStartAngle = normalizeAngle(targetStartAngle);
                targetEndAngle = normalizeAngle(targetEndAngle);

                console.log('[ECharts ZRender Update] Target angles (normalized):', {
                  startAngle: targetStartAngle,
                  endAngle: targetEndAngle,
                  startDegrees: (targetStartAngle * 180 / Math.PI),
                  endDegrees: (targetEndAngle * 180 / Math.PI),
                  originalStart: event.target.shape.startAngle,
                  originalEnd: event.target.shape.endAngle
                });

                // 각 데이터의 각도를 계산하여 매칭
                let clickedIndex = -1;
                let currentAngle = -Math.PI / 2; // ECharts 파이 차트는 12시 방향에서 시작
                const total = seriesData.reduce((sum, item) => sum + item.value, 0);

                console.log('[ECharts ZRender Update] Checking angles for each data item:');
                for (let i = 0; i < seriesData.length; i++) {
                  const item = seriesData[i];
                  const angle = (item.value / total) * Math.PI * 2;
                  const itemStartAngle = normalizeAngle(currentAngle);
                  const itemEndAngle = normalizeAngle(currentAngle + angle);

                  console.log(`[ECharts ZRender Update] Item ${i} (${item.name}):`, {
                    value: item.value,
                    percentage: (item.value / total * 100).toFixed(2) + '%',
                    startAngle: itemStartAngle,
                    endAngle: itemEndAngle,
                    startDegrees: (itemStartAngle * 180 / Math.PI),
                    endDegrees: (itemEndAngle * 180 / Math.PI),
                    startDiff: Math.abs(itemStartAngle - targetStartAngle),
                    endDiff: Math.abs(itemEndAngle - targetEndAngle)
                  });

                  // 각도 비교 (부동소수점 오차 고려)
                  if (Math.abs(itemStartAngle - targetStartAngle) < 0.01 &&
                      Math.abs(itemEndAngle - targetEndAngle) < 0.01) {
                    clickedIndex = i;
                    console.log('[ECharts ZRender Update] MATCH FOUND!');
                    break;
                  }
                  currentAngle = currentAngle + angle;
                }

                // 매칭이 안 되면 다른 방법 시도 - 더 간단한 방법으로 인덱스 찾기
                if (clickedIndex === -1) {
                  console.log('[ECharts ZRender Update] No exact match, trying simplified detection');

                  // ECharts 파이차트 옵션을 직접 확인
                  const pieOption = option.series[0];
                  const startAngleOption = pieOption.startAngle || 90; // 기본값 90도 (12시 방향)

                  // 클릭된 섹터의 중간 각도 계산 (원본 각도 사용)
                  const clickedMidAngle = (event.target.shape.startAngle + event.target.shape.endAngle) / 2;

                  // ECharts의 실제 시작 각도로 다시 계산
                  const startAngleRad = (startAngleOption * Math.PI) / 180;
                  currentAngle = startAngleRad;

                  for (let i = 0; i < seriesData.length; i++) {
                    const item = seriesData[i];
                    const angle = (item.value / total) * Math.PI * 2;
                    const itemStartAngle = currentAngle;
                    const itemEndAngle = currentAngle + angle;

                    console.log(`[ECharts ZRender Update] Checking item ${i} (${item.name}):`, {
                      itemRange: [itemStartAngle, itemEndAngle],
                      clickedMid: clickedMidAngle,
                      inRange: (clickedMidAngle >= itemStartAngle && clickedMidAngle <= itemEndAngle)
                    });

                    // 클릭된 중간 각도가 이 아이템의 범위에 있는지 확인
                    if (clickedMidAngle >= itemStartAngle && clickedMidAngle <= itemEndAngle) {
                      clickedIndex = i;
                      console.log(`[ECharts ZRender Update] Found by mid-angle! Item ${i} (${item.name})`);
                      break;
                    }
                    currentAngle = itemEndAngle;
                  }
                }

                // 여전히 못 찾으면 시작 각도를 조정해서 다시 시도
                if (clickedIndex === -1) {
                  console.log('[ECharts ZRender Update] Trying with different start angle (3 o\'clock position)');
                  currentAngle = 0; // 3시 방향에서 시작

                  for (let i = 0; i < seriesData.length; i++) {
                    const item = seriesData[i];
                    const angle = (item.value / total) * Math.PI * 2;
                    const itemStartAngle = currentAngle;
                    const itemEndAngle = currentAngle + angle;

                    if (Math.abs(itemStartAngle - targetStartAngle) < 0.1 &&
                        Math.abs(itemEndAngle - targetEndAngle) < 0.1) {
                      clickedIndex = i;
                      console.log(`[ECharts ZRender Update] Found with 3 o'clock start! Item ${i} (${item.name})`);
                      break;
                    }
                    currentAngle = itemEndAngle;
                  }
                }

                if (clickedIndex >= 0) {
                  const clickedItem = seriesData[clickedIndex];
                  const clickedData = {
                    seriesName: option.series[0].name || '',
                    name: clickedItem.name,
                    value: clickedItem.value,
                    dataIndex: clickedIndex,
                    seriesIndex: 0,
                    componentType: 'series',
                    chartType: 'pie',
                    data: clickedItem
                  };

                  console.log('[ECharts ZRender Update] Pie click processed:', clickedData);
                  if (onChartClick && typeof onChartClick === 'function') {
                    onChartClick(clickedData, 'self');
                  }
                }
              }
              // 바차트는 dataIndex가 없으므로 위치로 계산
              else if (event.target && event.target.type === 'rect' && chartData?.viz_type === 'bar') {
                console.log('[ECharts ZRender Update] Processing bar click by position');
                const option = chartInstanceRef.current.getOption();
                const xAxisData = option.xAxis[0]?.data || option.xAxis?.data || [];
                const seriesCount = (option.series || []).length;

                // 클릭된 바의 좌표
                const clickedX = event.target.shape?.x || 0;
                const clickedWidth = event.target.shape?.width || 0;
                const barCenterX = clickedX + clickedWidth / 2;

                // 그리드 영역 계산 (보통 차트의 10~90% 영역)
                const chartWidth = chartInstanceRef.current.getWidth();
                const gridLeft = chartWidth * 0.1;
                const gridRight = chartWidth * 0.9;
                const gridWidth = gridRight - gridLeft;

                // 각 카테고리의 예상 위치 계산
                const categoryCount = xAxisData.length || 1;
                const categoryWidth = gridWidth / categoryCount;

                // 카테고리 인덱스 탐지
                let clickedIndex = -1;
                for (let i = 0; i < categoryCount; i++) {
                  const expectedX = gridLeft + (i * categoryWidth) + (categoryWidth * 0.2);
                  const expectedEndX = expectedX + (categoryWidth * 0.6);
                  console.log(`[ECharts ZRender Update] Checking bar ${i} (${xAxisData[i]}):`, {
                    expectedX,
                    expectedEndX,
                    clickedX,
                    matches: (clickedX >= expectedX - 10 && clickedX <= expectedEndX + 10)
                  });
                  if (clickedX >= expectedX - 10 && clickedX <= expectedEndX + 10) {
                    clickedIndex = i;
                    console.log(`[ECharts ZRender Update] Found bar by position! Index ${i} (${xAxisData[i]})`);
                    break;
                  }
                }

                // y값 기준 보정 (필요시)
                if (clickedIndex === -1) {
                  console.log('[ECharts ZRender Update] Trying to match by center position');
                  for (let i = 0; i < categoryCount; i++) {
                    const groupStart = gridLeft + (i * categoryWidth) + (categoryWidth * 0.2);
                    const groupEnd = groupStart + (categoryWidth * 0.6);
                    const groupCenter = (groupStart + groupEnd) / 2;
                    if (Math.abs(barCenterX - groupCenter) < categoryWidth / 2) {
                      clickedIndex = i;
                      console.log(`[ECharts ZRender Update] Found by center! Index ${i} (${xAxisData[i]})`);
                      break;
                    }
                  }
                }

                if (clickedIndex >= 0) {
                  // 그룹 내 시리즈 인덱스 추정
                  const groupStart = gridLeft + (clickedIndex * categoryWidth) + (categoryWidth * 0.2);
                  const groupWidth = categoryWidth * 0.6;
                  const perSeriesWidth = groupWidth / Math.max(seriesCount, 1);
                  let seriesIndex = Math.floor((barCenterX - groupStart) / perSeriesWidth);
                  if (seriesIndex < 0) seriesIndex = 0;
                  if (seriesIndex > seriesCount - 1) seriesIndex = seriesCount - 1;

                  const series = option.series[seriesIndex] || {};
                  const seriesData = series.data || [];
                  const rawValue = seriesData[clickedIndex];
                  const actualValue = (typeof rawValue === 'object' && rawValue !== null && 'value' in rawValue) ? rawValue.value : rawValue;

                  const clickedData = {
                    seriesName: series.name || '',
                    name: xAxisData[clickedIndex],
                    value: actualValue,
                    dataIndex: clickedIndex,
                    seriesIndex: seriesIndex,
                    componentType: 'series',
                    chartType: 'bar',
                    data: { name: xAxisData[clickedIndex], value: actualValue }
                  };

                  console.log('[ECharts ZRender Update] Bar click processed:', clickedData);
                  if (onChartClick && typeof onChartClick === 'function') {
                    onChartClick(clickedData, 'self');
                  }
                }
              }
              // 라인차트 처리 - 픽셀→데이터 좌표 변환 기반 (circle 또는 path)
              else if (event.target && (event.target.type === 'circle' || event.target.type === 'path') && chartData?.viz_type === 'line') {
                console.log('[ECharts ZRender Update] Processing line click by position');
                const option = chartInstanceRef.current.getOption();

                // x축/시리즈 데이터
                const xAxisData = option.xAxis[0]?.data || option.xAxis?.data;
                const seriesData = option.series[0]?.data || [];

                // 클릭 좌표 (픽셀)
                const domRect = chartInstanceRef.current.getDom().getBoundingClientRect();
                const clickX = (typeof event.offsetX === 'number')
                  ? event.offsetX
                  : (typeof event.zrX === 'number')
                    ? event.zrX
                    : (event.event && typeof event.event.pageX === 'number')
                      ? (event.event.pageX - domRect.left)
                      : (event.target.shape && typeof event.target.shape.cx === 'number')
                        ? event.target.shape.cx
                        : 0;
                const clickY = (typeof event.offsetY === 'number')
                  ? event.offsetY
                  : (typeof event.zrY === 'number')
                    ? event.zrY
                    : (event.event && typeof event.event.pageY === 'number')
                      ? (event.event.pageY - domRect.top)
                      : (event.target.shape && typeof event.target.shape.cy === 'number')
                        ? event.target.shape.cy
                        : 0;

                // 픽셀→데이터 좌표 변환
                const dataCoord = chartInstanceRef.current.convertFromPixel({ seriesIndex: 0 }, [clickX, clickY]);
                let clickedIndex = 0;
                if (Array.isArray(dataCoord)) {
                  const xCoord = dataCoord[0];
                  if (typeof xCoord === 'number' && !Number.isNaN(xCoord)) {
                    // 세그먼트(path) 클릭 시에는 좌측 포인트로 bias, 점(circle)은 최근접 반올림
                    if (event.target.type === 'path') {
                      clickedIndex = Math.floor(xCoord + 0.00001);
                    } else {
                      clickedIndex = Math.round(xCoord);
                    }
                  } else if (xAxisData && typeof xCoord === 'string') {
                    const idx = xAxisData.indexOf(xCoord);
                    clickedIndex = idx >= 0 ? idx : 0;
                  }
                }

                // 범위 보정
                const pointCount = xAxisData ? xAxisData.length : seriesData.length;
                if (clickedIndex < 0) clickedIndex = 0;
                if (clickedIndex > pointCount - 1) clickedIndex = pointCount - 1;

                // 값 추출
                const rawValue = seriesData[clickedIndex];
                let actualValue;
                let xValue = xAxisData ? xAxisData[clickedIndex] : clickedIndex;
                if (typeof rawValue === 'object' && rawValue !== null) {
                  if (Array.isArray(rawValue)) {
                    xValue = rawValue[0];
                    actualValue = rawValue[1];
                  } else if ('value' in rawValue) {
                    if (Array.isArray(rawValue.value)) {
                      xValue = rawValue.value[0];
                      actualValue = rawValue.value[1];
                    } else {
                      actualValue = rawValue.value;
                    }
                  } else {
                    actualValue = rawValue;
                  }
                } else {
                  actualValue = rawValue;
                }

                const clickedData = {
                  seriesName: option.series[0]?.name || '',
                  name: xValue,
                  value: actualValue,
                  dataIndex: clickedIndex,
                  seriesIndex: 0,
                  componentType: 'series',
                  chartType: 'line',
                  data: { name: xValue, value: actualValue }
                };

                console.log('[ECharts ZRender Update] Line click processed:', clickedData);
                if (onChartClick && typeof onChartClick === 'function') {
                  onChartClick(clickedData, 'self');
                }
              }
              // 기존 조건문 - 바차트 등 dataIndex가 있는 차트들 (실제로는 실행되지 않을 것)
              else if (event.target && event.target.dataIndex !== undefined) {
                console.log('[ECharts ZRender Update] Processing click with dataIndex:', event.target.dataIndex);
                const option = chartInstanceRef.current.getOption();

                // 파이차트 처리 (이 부분은 실행되지 않을 것)
                if (chartData?.viz_type === 'pie') {
                  const seriesData = option.series[0].data;
                  const clickedItem = seriesData[event.target.dataIndex];

                  if (clickedItem) {
                    const clickedData = {
                      seriesName: option.series[0].name || '',
                      name: clickedItem.name,
                      value: clickedItem.value,
                      dataIndex: event.target.dataIndex,
                      seriesIndex: 0,
                      componentType: 'series',
                      chartType: chartData?.viz_type,
                      data: clickedItem
                    };

                    console.log('[ECharts ZRender Update] Processing pie click:', clickedData);
                    if (onChartClick && typeof onChartClick === 'function') {
                      onChartClick(clickedData, 'self');
                    }
                  }
                }
                // 바차트 처리
                else if (chartData?.viz_type === 'bar') {
                  const seriesIndex = event.target.seriesIndex || 0;
                  const series = option.series[seriesIndex];
                  const categories = option.xAxis.data || option.xAxis[0].data;

                  const clickedData = {
                    seriesName: series.name || '',
                    name: categories ? categories[event.target.dataIndex] : '',
                    value: series.data[event.target.dataIndex],
                    dataIndex: event.target.dataIndex,
                    seriesIndex: seriesIndex,
                    componentType: 'series',
                    chartType: chartData?.viz_type,
                    data: null
                  };

                  console.log('[ECharts ZRender Update] Processing bar click:', clickedData);
                  if (onChartClick && typeof onChartClick === 'function') {
                    onChartClick(clickedData, 'self');
                  }
                }
              }
            });
          }

          if (debugMode) console.log('[ECharts Update] ✅ Click event listener re-registered after delay!');

        } catch (error) {
          console.error('[ECharts Update] ❌ Failed to re-register click event listener:', error);
        }
      }, 100);
    }
  }, [isEditMode, onChartDoubleClick, onChartClick, debugMode, chartData?.viz_type, printMode]); // printMode 추가

  // lastUpdated 변경 감지하여 업데이트 애니메이션 표시 및 ready 제어
  useEffect(() => {
    if (lastUpdated && lastUpdatedRef.current && lastUpdated !== lastUpdatedRef.current) {
      // 타이머 갱신(skipUpdateAnimation=true)일 때는 애니메이션 스킵
      if (chartData?.skipUpdateAnimation) {
        // 애니메이션 없이 즉시 완료 처리
        if (onRenderComplete) {
          onRenderComplete();
        }
      } else {
        setBusy(true);
        setIsUpdating(true);
        // 애니메이션 시간을 조금 더 길게 조정 (500ms)
        const timer = setTimeout(() => {
          setIsUpdating(false);
          setBusy(false);
          // 렌더링 완료 콜백 호출
          if (onRenderComplete) {
            onRenderComplete();
          }
        }, 500);
        return () => clearTimeout(timer);
      }
    }
    if (lastUpdated && !lastUpdatedRef.current) {
      setBusy(false);
      // 초기 렌더링 완료 콜백 호출
      if (onRenderComplete) {
        onRenderComplete();
      }
    }
    lastUpdatedRef.current = lastUpdated;
  }, [lastUpdated, setBusy, onRenderComplete]);

  // 차트 데이터 변경 시에만 setOption 호출하는 별도 useEffect
  useEffect(() => {
    if (!chartData || !chartInstanceRef.current) {
      return;
    }

    // ECharts 타입이 아닌 경우 스킵
    if (['table', 'navermap', 'badge', 'text_label', 'webframe', 'battery_grid', 'progress_bar'].includes(chartData.viz_type)) {
      return;
    }

    try {
      setBusy(true);
      if (debugMode) console.log('[ECharts DataUpdate] Updating chart data for:', chartData.viz_type);

      const option = generateStandardizedChartOption(chartData, isEditMode);
      chartInstanceRef.current.setOption(option, true);
      baseOptionRef.current = option;
      if (debugMode) console.log('[ECharts DataUpdate] Chart data updated successfully.');

      const inst = chartInstanceRef.current;
      const handleFinished = () => {
        setBusy(false);
        try { inst.off('finished', handleFinished); } catch (_) {}
      };
      try { inst.on('finished', handleFinished); } catch (_) { setBusy(false); }
    } catch (updateError) {
      console.error('[ECharts DataUpdate] Error updating chart data:', updateError);
      setError(`차트 데이터 업데이트 중 오류 발생: ${updateError.message}`);
      setBusy(false);
    }
  }, [chartData, isEditMode, debugMode]); // 데이터 변경 시에만 실행

  /**
   * 차트 크기에 따른 동적 grid 여백 계산
   */
  const calculateDynamicGrid = (containerWidth, containerHeight, hasYAxisTitle, hasXAxisTitle, vizType) => {
    // 작은 차트일수록 더 큰 여백 비율 필요 (여백 최소화)
    let left = 50;  // 기본 픽셀값
    let right = 30;
    let bottom = 40;
    let top = 30;

    // 스캐터 차트는 더 타이트한 여백 사용
    const isScatter = vizType === 'scatter';

    // Y축 제목이 있으면 왼쪽 여백 증가
    if (hasYAxisTitle) {
      if (containerWidth < 300) {
        left = isScatter ? 65 : 75;  // 매우 작은 차트
      } else if (containerWidth < 500) {
        left = isScatter ? 55 : 65;   // 작은 차트
      } else {
        left = isScatter ? 50 : 60;   // 일반 차트
      }
    }

    // X축 제목이 있으면 하단 여백 증가 (축 라벨 + 축 제목 텍스트 공간 확보)
    if (hasXAxisTitle) {
      if (containerHeight < 300) {
        bottom = isScatter ? 58 : 65;
      } else if (containerHeight < 400) {
        bottom = isScatter ? 55 : 62;
      } else {
        bottom = isScatter ? 52 : 60;
      }
    }

    return { left, right, bottom, top, containLabel: true };
  };

  /**
   * 표준화된 차트 데이터 구조를 ECharts 옵션으로 변환
   * @param {Object} standardChartData - 표준화된 차트 데이터
   * @param {boolean} isEditMode - 편집 모드 여부
   * @returns {Object} ECharts 옵션 객체
   */
  const generateStandardizedChartOption = (standardChartData, isEditMode) => {
    const { viz_type, data, options = {} } = standardChartData;

    // 컨테이너 크기 측정 (동적 grid 계산용)
    const containerWidth = containerRef.current?.offsetWidth || 500;
    const containerHeight = containerRef.current?.offsetHeight || 400;
    const hasYAxisTitle = !!(options.axes?.y);
    const hasXAxisTitle = !!(options.axes?.x);

    // 동적 grid 계산
    const dynamicGrid = calculateDynamicGrid(containerWidth, containerHeight, hasYAxisTitle, hasXAxisTitle, viz_type);

    // 기본 옵션 설정
    const baseOption = {
      // showTitle 옵션 추가 - 기본값 true, false일 경우 title을 undefined로 설정
      ...(options.showTitle !== false && {
        title: {
          text: options.title || '',
          left: 'center',
          top: isEditMode ? '5%' : '4%',
          textStyle: {
            fontSize: 20,
            fontWeight: '600',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            color: '#2c3e50',
            lineHeight: 1.2,
            padding: [0, 0, 8, 0]
          }
        }
      }),
      tooltip: (isEditMode || printMode) ? {
        show: false  // 편집/인쇄 모드에서는 툴팁 비활성화
      } : {
        trigger: viz_type === 'pie' ? 'item' : 'axis',
        confine: true,
        triggerOn: 'mousemove|click',  // 마우스 이동과 클릭 모두 지원
        enterable: false,  // 툴팁에 마우스 진입 방지
        renderMode: 'html',  // HTML 렌더링 모드 명시
        appendToBody: true,  // body에 툴팁 추가
        // 툴팁이 클릭을 가로채지 않도록 보장
        extraCssText: 'pointer-events: none; z-index: 9999;'
      },
      grid: dynamicGrid,  // 차트 크기에 따라 동적으로 계산된 여백
      // 인쇄 모드에서는 애니메이션을 전역적으로 비활성화하여 finished 이벤트를 빠르게 유도
      animation: !printMode,
      animationDuration: printMode ? 0 : 300,
      animationDurationUpdate: printMode ? 0 : 300,
      animationEasing: printMode ? 'linear' : 'cubicOut',
      animationEasingUpdate: printMode ? 'linear' : 'cubicOut'
    };

    // 축 제목 설정
    if (options.axes?.x) {
      baseOption.xAxis = {
        nameTextStyle: {
          fontSize: 18,
          fontWeight: '600',
          color: '#2c3e50',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        },
        axisLabel: {
          fontSize: 12,
          color: '#666'
        }
      };
    }

    if (options.axes?.y) {
      baseOption.yAxis = {
        nameTextStyle: {
          fontSize: 18,
          fontWeight: '600',
          color: '#2c3e50',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        },
        axisLabel: {
          fontSize: 12,
          color: '#666'
        }
      };
    }

    if (debugMode) {
      console.log(`[Generate Option] For ${viz_type}:`, { title: options.title, data });
    }

    // 차트 크기 정보를 options에 추가 (동적 조정용)
    const optionsWithSize = {
      ...options,
      __containerWidth: containerWidth,
      __containerHeight: containerHeight
    };

    // 차트 유형에 따른 세부 옵션 설정
    try {
      let finalOption;
      switch (viz_type) {
        case 'line':
          finalOption = generateLineChartOption(data, baseOption, optionsWithSize, isEditMode);
          break;
        case 'bar':
          finalOption = generateBarChartOption(data, baseOption, optionsWithSize, isEditMode);
          break;
        case 'pie':
          finalOption = generatePieChartOption(data, baseOption, optionsWithSize);
          break;
        case 'scatter':
          finalOption = generateScatterChartOption(data, baseOption, optionsWithSize);
          break;
        case 'heatmap':
          finalOption = generateHeatmapChartOption(data, baseOption, optionsWithSize);
          break;
        case 'gauge':
          finalOption = generateGaugeChartOption(data, baseOption, optionsWithSize);
          break;
        case 'radar':
          finalOption = generateRadarChartOption(data, baseOption, optionsWithSize);
          break;
        default:
          console.warn(`[Generate Option] Fallback: Unsupported type '${viz_type}' reached generateChartOption. Returning basic bar.`);
          finalOption = generateBarChartOption(data || {}, baseOption, options, isEditMode);
          break;
      }
      // 개별 시리즈에도 인쇄 모드 시 애니메이션 비활성화 적용
      try {
        if (printMode && finalOption && Array.isArray(finalOption.series)) {
          finalOption.series = finalOption.series.map(s => ({
            ...s,
            animation: false,
            animationDuration: 0,
            animationDurationUpdate: 0
          }));
        }
      } catch (_) {}
      baseOptionRef.current = finalOption;
      return finalOption;
    } catch (error) {
      console.error('[Generate Option] Error:', error);
      setError(`차트 옵션 생성 오류: ${error.message}`);
      return {
        title: { text: options.title || '오류', subtext: '옵션 생성 실패', left: 'center' },
        grid: { containLabel: true },
        xAxis: { type: 'category', data: ['오류'] },
        yAxis: { type: 'value' },
        series: [{ type: 'bar', data: [0] }]
      };
    }
  };

// 최종 Return 문 (조건부 렌더링)
// 배경 스타일 적용 로직
const backgroundStyle = {};
// transparent 옵션 체크: chart_options.transparent (최상위) 또는 chart_options.options.transparent 양쪽 모두 확인
const transparentValue = chartData?.transparent ?? chartData?.options?.transparent;
const isTransparent = transparentValue === true || transparentValue === 'true';

if (chartData?.options?.style?.background) {
  const bg = chartData.options.style.background;
  // transparent가 true인 경우 배경색을 무시하고 투명 처리
  // transparent가 false이거나 없는 경우에만 background.color 적용
  if (!isTransparent && bg.color) {
    backgroundStyle.backgroundColor = bg.color;
  }
  if (bg.borderRadius !== undefined) {
    backgroundStyle.borderRadius = `${bg.borderRadius}px`;
  }
}

// transparent가 true인 경우 명시적으로 투명 설정 (background.color 오버라이드)
if (isTransparent) {
  backgroundStyle.backgroundColor = 'transparent';
}

return (
  <>
    <style>
      {`
        @keyframes fadeInOut {
          0% { opacity: 0; }
          50% { opacity: 1; }
          100% { opacity: 0; }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .chart-update-overlay {
          position: absolute;
          inset: 0;
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(3px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 99;
          animation: fadeIn 0.2s ease-in;
          pointer-events: none;
          border-radius: inherit;
        }
        
        .chart-update-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid rgba(0, 123, 255, 0.2);
          border-top-color: #007bff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        
        .chart-updating {
          transition: filter 0.3s ease;
        }
      `}
    </style>
    <div 
      className={`dynamic-chart-container ${className} ${isUpdating ? 'chart-updating' : ''}`} 
      ref={containerRef}
      data-viz-type={chartData?.viz_type}
      style={{ 
        position: 'relative', 
        width: '100%', 
        height: '100%', 
        // 고정 최소 높이는 그리드 비율을 깨뜨릴 수 있어 제거
        minHeight: 'auto',
        aspectRatio: chartData?.viz_type === 'pie' ? '1 / 1' : 'auto',
        padding: '0',
        margin: '0',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: '0',
        boxShadow: 'none',
        overflow: 'visible',
        ...backgroundStyle, // 공통 배경 스타일 적용
        ...style 
      }}
  >
    {/* 업데이트 오버레이 - 변수 구독/데이터 갱신 시 표시 (투명 차트는 배경 제거하고 스피너만 표시) */}
    {isUpdating && (
      <div 
        className="chart-update-overlay"
        style={{ 
          background: isTransparent ? 'transparent' : undefined,
          backdropFilter: isTransparent ? 'none' : undefined
        }}
      >
        <div className="chart-update-spinner" />
      </div>
    )}
    
    {/* 범용 리셋 버튼 - on_clicked_other로 변경된 차트에만 표시 */}
    {onChartClick && isActiveChart && chartData?.options?.title && chartData?.viz_type !== 'badge' && (
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        zIndex: 100
      }}>
        <button
          onClick={() => onChartClick({ type: 'reset' }, 'self')}
          style={{
            padding: '4px 12px',
            fontSize: '13px',
            fontWeight: '500',
            color: '#495057',
            backgroundColor: '#ffffff',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#e9ecef';
            e.target.style.borderColor = '#adb5bd';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#ffffff';
            e.target.style.borderColor = '#dee2e6';
          }}
        >
          리셋
        </button>
      </div>
    )}
    {/* 테이블 타입 렌더링 */}
    {chartData?.viz_type === 'table' ? (
      <div style={{ width: '100%', height: '100%', margin: 0, padding: 0 }}>
                <TableRenderer
          chartData={chartData}
          error={error}
          onChartClick={onChartClick}
          isEditMode={isEditMode}
          componentId={componentId}
          onEmergencyTrigger={onEmergencyTrigger}
          activeQuery={activeQuery}
          style={chartData?.options?.style}
        />
      </div>
    ) : chartData?.viz_type === 'navermap' ? (
      /* 네이버 지도 타입 렌더링 */
      <NaverMapRenderer chartData={chartData} error={error} debugMode={debugMode} />
    ) : chartData?.viz_type === 'badge' ? (
      /* 배지 타입 렌더링 */
      <BadgeRenderer chartData={chartData} error={error} onChartClick={onChartClick} isEditMode={isEditMode} componentId={componentId} onEmergencyTrigger={onEmergencyTrigger} isClicked={isClicked} isSelected={isSelected} />
    ) : chartData?.viz_type === 'text_label' ? (
      /* 텍스트 라벨 타입 렌더링 */
      <TextLabelRenderer chartData={chartData} error={error} isEditMode={isEditMode} isSelected={isSelected} />
    ) : chartData?.viz_type === 'webframe' ? (
      /* 웹프레임 타입 렌더링 */
      <WebFrameRenderer chartData={chartData} error={error} isEditMode={isEditMode} onChartClick={onChartDoubleClick} />
    ) : chartData?.viz_type === 'battery_grid' ? (
      /* 배터리 그리드 타입 렌더링 - key를 사용하여 옵션 변경 시 컴포넌트 재마운트 */
      <BatteryGridRenderer 
        key={`battery-grid-${JSON.stringify(chartData?.options?.style?.visibility)}-${chartData?.options?.showTitle}`}
        chartData={chartData} 
        error={error} 
        onChartClick={onChartClick} 
        isEditMode={isEditMode} 
      />
    ) : chartData?.viz_type === 'progress_bar' ? (
      /* 프로그레스 바 타입 렌더링 */
      <ProgressBarRenderer chartData={chartData} error={error} isEditMode={isEditMode} />
    ) : chartData?.viz_type === 'date_picker' ? (
      /* DatePicker 타입 렌더링 */
      <DatePickerRenderer 
        chartData={chartData} 
        error={error} 
        onChartClick={onChartClick} 
        isEditMode={isEditMode} 
        style={chartData?.options?.style || {}}
        forceResize={forceResize}
      />
    ) : (
      /* ECharts 타입 렌더링 */
      <>
        {/* ECharts 컨테이너 */}
        <div ref={chartRef} style={{ 
          width: '100%', 
          height: '100%', 
          borderRadius: '0', 
          overflow: 'visible',
          opacity: (isUpdating && !printMode) ? 0.3 : 1,
          transition: 'opacity 0.3s ease-in-out'
        }}></div>
        {/* ECharts 오류 메시지 */}
        {error && (
             <div style={{
                 position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                 display: 'flex', justifyContent: 'center', alignItems: 'center',
                 backgroundColor: 'rgba(255, 255, 255, 0.95)',
                 color: '#e74c3c',
                 padding: '20px', 
                 textAlign: 'center', 
                 borderRadius: '8px',
                 fontSize: '14px',
                 fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                 zIndex: 10
             }}>
                 <strong>차트 오류:</strong> {error}
             </div>
        )}
      </>
    )}
  </div>
  </>
);

};

// React.memo로 컴포넌트 감싸기 (엄격한 비교 함수 추가)
export default memo(DynamicChartComponent, (prevProps, nextProps) => {
  // 차트 데이터 비교
  const prevChartData = prevProps.chartData;
  const nextChartData = nextProps.chartData;
  
  // props 비교
  return (
    prevChartData?.viz_type === nextChartData?.viz_type &&
    prevChartData?.lastUpdated === nextChartData?.lastUpdated &&
    JSON.stringify(prevChartData?.data) === JSON.stringify(nextChartData?.data) &&
    JSON.stringify(prevChartData?.options) === JSON.stringify(nextChartData?.options) &&
    prevProps.className === nextProps.className &&
    JSON.stringify(prevProps.style) === JSON.stringify(nextProps.style) &&
    prevProps.debugMode === nextProps.debugMode &&
    prevProps.forceResize === nextProps.forceResize &&
    prevProps.isEditMode === nextProps.isEditMode &&
    prevProps.isActiveChart === nextProps.isActiveChart &&
    prevProps.isClicked === nextProps.isClicked &&
    prevProps.componentId === nextProps.componentId &&
    prevProps.onChartDoubleClick === nextProps.onChartDoubleClick &&
    prevProps.onChartClick === nextProps.onChartClick &&
    prevProps.onEmergencyTrigger === nextProps.onEmergencyTrigger &&
    JSON.stringify(prevProps.activeQuery) === JSON.stringify(nextProps.activeQuery)
  );
}); 